import { browser } from '$app/env';
import { derived, get, writable } from 'svelte/store';

export type DiagnosticSeverity = 'error' | 'warning' | 'info';
export type DiagnosticCategory = 'api' | 'runtime' | 'websocket' | 'optimistic';
export type DiagnosticFilter = 'all' | 'errors' | 'warnings' | 'api' | 'runtime';

interface DiagnosticEntry {
	id: string;
	timestamp: string;
	severity: DiagnosticSeverity;
	category: DiagnosticCategory;
	title: string;
	message: string;
	details?: Record<string, unknown> | string | null;
	url?: string;
	stack?: string;
	isThirdParty?: boolean;
}

const MAX_ENTRIES = 100;

export const diagnosticsStore = writable<DiagnosticEntry[]>([]);

export const errorCount = derived(
	diagnosticsStore,
	($list) => $list.filter((entry) => entry.severity === 'error').length
);

export const warningCount = derived(
	diagnosticsStore,
	($list) => $list.filter((entry) => entry.severity === 'warning').length
);

export const hasErrors = derived(errorCount, ($count) => $count > 0);

export const recentErrors = derived(diagnosticsStore, ($list) =>
	$list.filter((entry) => entry.severity === 'error').slice(0, 5)
);

export const isDiagnosticsModalOpen = writable<boolean>(false);

export function openDiagnostics(): void {
	isDiagnosticsModalOpen.set(true);
}

export function closeDiagnostics(): void {
	isDiagnosticsModalOpen.set(false);
}

interface NavigationBreadcrumb {
	from: string | null;
	to: string;
	timestamp: string;
}

export const navigationHistoryStore = writable<NavigationBreadcrumb[]>([]);

export function recordNavigation(from: string | null, to: string): void {
	if (!to) return;
	const entry: NavigationBreadcrumb = {
		from,
		to,
		timestamp: new Date().toISOString()
	};
	navigationHistoryStore.update((history) => {
		const updated = [...history, entry];
		return updated.slice(-10);
	});
}

const SENSITIVE_KEYS = new Set([
	'password',
	'token',
	'secret',
	'creditcard',
	'cardnumber',
	'cvc',
	'cvv',
	'authorization',
	'refreshtoken',
	'accesstoken'
]);

export function sanitizeDetails(
	data: unknown,
	seen: WeakSet<object> = new WeakSet<object>(),
	depth = 0
): unknown {
	if (data === null || data === undefined) {
		return data;
	}
	if (typeof data !== 'object') {
		return data;
	}
	if (depth > 6) {
		return '[Max Depth Reached]';
	}
	if (seen.has(data as object)) {
		return '[Circular Reference]';
	}
	seen.add(data as object);

	if (Array.isArray(data)) {
		return data.slice(0, 50).map((item) => sanitizeDetails(item, seen, depth + 1));
	}

	const sanitized: Record<string, unknown> = {};
	try {
		for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
			if (SENSITIVE_KEYS.has(key.toLowerCase())) {
				sanitized[key] = '***REDACTED***';
			} else if (typeof value === 'object' && value !== null) {
				sanitized[key] = sanitizeDetails(value, seen, depth + 1);
			} else {
				sanitized[key] = value;
			}
		}
	} catch {
		return String(data);
	}
	return sanitized;
}

const THIRD_PARTY_PATTERNS = [
	'crisp.chat',
	'client.crisp.chat',
	'googletagmanager.com',
	'google-analytics.com',
	'facebook.net',
	'hotjar',
	'clarity.ms',
	'intercom',
	'stripe.com',
	'chrome-extension://',
	'moz-extension://',
	'safari-extension://'
];

interface FilteredErrorRule {
	eventType?: 'error' | 'unhandledrejection';
	errorType?: string;
	message?: string;
}

export const FILTERED_ERRORS: readonly FilteredErrorRule[] = [
	{
		eventType: 'error',
		errorType: 'ErrorEvent',
		message: 'Script error.'
	},
	{
		errorType: 'NotAllowedError',
		message:
			'The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.'
	},
	{
		errorType: 'NotAllowedError',
		message: "play() failed because the user didn't interact with the document first."
	}
];

export function isFilteredError(options: {
	eventType?: 'error' | 'unhandledrejection';
	errorType?: string;
	message?: string;
}): boolean {
	const normalizedMessage = options.message?.trim();
	return FILTERED_ERRORS.some((rule) => {
		if (rule.eventType && options.eventType && rule.eventType !== options.eventType) {
			return false;
		}
		if (rule.errorType) {
			if (options.errorType) {
				if (rule.errorType !== options.errorType) {
					return false;
				}
			} else if (!rule.message || normalizedMessage !== rule.message.trim()) {
				return false;
			}
		}
		if (rule.message) {
			const expected = rule.message.trim();
			const matchesExact =
				normalizedMessage === expected || normalizedMessage === `Error: ${expected}`;
			if (!matchesExact) {
				return false;
			}
		}
		return true;
	});
}

export function isThirdPartyIncident(data: {
	url?: string;
	stack?: string;
	message?: string;
	title?: string;
}): boolean {
	const haystack =
		`${data.url ?? ''} ${data.stack ?? ''} ${data.message ?? ''} ${data.title ?? ''}`.toLowerCase();
	return THIRD_PARTY_PATTERNS.some((pattern) => haystack.includes(pattern));
}

let entryCounter = 0;
let pendingEntries: DiagnosticEntry[] = [];
let flushScheduled = false;

function flushDiagnosticsSync(): void {
	if (pendingEntries.length === 0) return;
	const batch = pendingEntries;
	pendingEntries = [];
	flushScheduled = false;

	diagnosticsStore.update((entries) => {
		const updated = [...batch, ...entries];
		if (updated.length > MAX_ENTRIES) {
			return updated.slice(0, MAX_ENTRIES);
		}
		return updated;
	});
}

export function addDiagnostic(entry: Omit<DiagnosticEntry, 'id' | 'timestamp'>): void {
	entryCounter++;
	const isThirdParty = entry.isThirdParty ?? isThirdPartyIncident(entry);
	const newEntry: DiagnosticEntry = {
		...entry,
		id: `diag-${Date.now()}-${entryCounter}`,
		timestamp: new Date().toISOString(),
		details: sanitizeDetails(entry.details) as Record<string, unknown> | string | null,
		isThirdParty
	};

	pendingEntries.unshift(newEntry);

	if (!flushScheduled) {
		flushScheduled = true;
		if (typeof window !== 'undefined') {
			setTimeout(flushDiagnosticsSync, 0);
		} else {
			flushDiagnosticsSync();
		}
	}
}

export function clearDiagnostics(): void {
	pendingEntries = [];
	flushScheduled = false;
	diagnosticsStore.set([]);
	navigationHistoryStore.set([]);
}

interface DiagnosticApiLogInput {
	method?: string;
	url?: string;
	payload?: unknown;
	timestamp?: string;
	status?: number;
	durationMs?: number;
	response?: unknown;
	errorResponse?: unknown;
	isError?: boolean;
}

export function exportDiagnosticsReport(
	apiLogs?: (DiagnosticApiLogInput | string)[],
	navigationHistory?: NavigationBreadcrumb[]
): string {
	flushDiagnosticsSync();
	const entries = get(diagnosticsStore);
	const errors = entries.filter((e) => e.severity === 'error');
	const warnings = entries.filter((e) => e.severity === 'warning');
	const thirdPartyCount = entries.filter((e) => e.isThirdParty).length;
	const appErrors = errors.filter((e) => !e.isThirdParty).length;
	const appWarnings = warnings.filter((e) => !e.isThirdParty).length;

	const lines: string[] = [
		'# Reservatoo Frontend Diagnostic Report',
		'',
		`- **Generated At:** ${new Date().toISOString()}`,
		`- **Total Incidents:** ${entries.length}${thirdPartyCount > 0 ? ` (including ${thirdPartyCount} 3rd-party)` : ''}`,
		`- **Errors:** ${errors.length}${thirdPartyCount > 0 ? ` (${appErrors} app, ${errors.length - appErrors} 3rd-party)` : ''}`,
		`- **Warnings:** ${warnings.length}${thirdPartyCount > 0 ? ` (${appWarnings} app, ${warnings.length - appWarnings} 3rd-party)` : ''}`,
		`- **Current URL:** ${browser ? window.location.href : 'N/A'}`,
		`- **User Agent:** ${browser ? navigator.userAgent : 'N/A'}`,
		`- **Screen:** ${browser ? `${window.innerWidth}x${window.innerHeight}` : 'N/A'}`,
		''
	];

	const history = navigationHistory ?? get(navigationHistoryStore);
	if (history.length > 0) {
		lines.push('## Navigation History (Breadcrumbs)');
		lines.push('');
		for (const nav of history) {
			const time = nav.timestamp.includes('T')
				? nav.timestamp.split('T')[1]?.slice(0, 8)
				: nav.timestamp;
			const fromPart = nav.from ? `\`${nav.from}\`` : '*(initial load)*';
			lines.push(`- **${time}**: ${fromPart} ➔ \`${nav.to}\``);
		}
		lines.push('');
	}

	lines.push('## Incident Log');
	lines.push('');

	if (entries.length === 0) {
		lines.push('_No incidents recorded._');
	} else {
		for (const entry of entries) {
			const badge =
				entry.severity === 'error'
					? '🔴 ERROR'
					: entry.severity === 'warning'
						? '🟠 WARN'
						: '🔵 INFO';
			const thirdPartyTag = entry.isThirdParty ? ' [3rd-party]' : '';
			lines.push(
				`### [${badge}]${thirdPartyTag} ${entry.category.toUpperCase()}: ${entry.title}`
			);
			lines.push(`- **Time:** ${entry.timestamp}`);
			lines.push(`- **Message:** ${entry.message}`);
			if (entry.url) {
				lines.push(`- **URL:** ${entry.url}`);
			}
			if (entry.details) {
				lines.push('```json');
				try {
					lines.push(JSON.stringify(entry.details, null, 2));
				} catch {
					lines.push(String(entry.details));
				}
				lines.push('```');
			}
			if (entry.stack) {
				lines.push('**Stack trace:**');
				lines.push('```');
				lines.push(entry.stack);
				lines.push('```');
			}
			lines.push('');
		}
	}

	if (apiLogs && apiLogs.length > 0) {
		lines.push('## Recent API Requests');
		lines.push('');
		const recent = apiLogs.slice(-15).reverse();
		for (const log of recent) {
			if (typeof log === 'string') {
				lines.push(`- \`${log}\``);
			} else {
				const method = (log.method || 'GET').toUpperCase();
				const url = log.url || '';
				const time = log.timestamp
					? log.timestamp.includes('T')
						? log.timestamp.split('T')[1]?.slice(0, 8)
						: log.timestamp
					: '';
				const statusText =
					log.status !== undefined ? ` [${log.status === 0 ? 'ERR' : log.status}]` : '';
				const durationFormatted =
					log.durationMs !== undefined
						? log.durationMs >= 1000
							? `${(log.durationMs / 1000).toFixed(1)}s`
							: `${log.durationMs}ms`
						: '';
				const durationText = durationFormatted ? ` (${durationFormatted})` : '';
				lines.push(
					`- **${method}** \`${url}\`${statusText} ${time ? `${time}` : ''}${durationText}`
				);
				if (log.payload !== undefined && log.payload !== null) {
					lines.push('```json');
					try {
						lines.push(JSON.stringify(log.payload, null, 2));
					} catch {
						lines.push(String(log.payload));
					}
					lines.push('```');
				}
				const responseData = log.response ?? log.errorResponse;
				const isErr =
					log.isError ??
					(log.status !== undefined && (log.status >= 400 || log.status === 0));
				if (responseData !== undefined && responseData !== null) {
					lines.push(isErr ? '**Error Response:**' : '**Response:**');
					lines.push('```json');
					try {
						lines.push(
							typeof responseData === 'string'
								? responseData
								: JSON.stringify(responseData, null, 2)
						);
					} catch {
						lines.push(String(responseData));
					}
					lines.push('```');
				}
			}
		}
		lines.push('');
	}

	return lines.join('\n');
}

let originalConsole: {
	error: typeof console.error;
	warn: typeof console.warn;
	info: typeof console.info;
	log: typeof console.log;
	debug: typeof console.debug;
} | null = null;

let isCapturingConsole = false;

function extractCallerStack(): string | undefined {
	const rawStack = new Error().stack;
	if (!rawStack) return undefined;
	const lines = rawStack.split('\n');
	// Filter out internal diagnostics frames
	const filtered = lines.filter(
		(line) =>
			!line.includes('diagnostics.ts') &&
			!line.includes('formatConsoleArgs') &&
			!line.includes('interceptConsoleMethod')
	);
	if (filtered.length > 0) {
		if (!filtered[0].trim().startsWith('Error')) {
			filtered.unshift('Call Stack:');
		}
		return filtered.slice(0, 15).join('\n');
	}
	return undefined;
}

function formatConsoleArgs(args: unknown[]): {
	title: string;
	message: string;
	stack?: string;
	details?: Record<string, unknown> | string | null;
} {
	if (args.length === 0) {
		return { title: 'Console output', message: '' };
	}

	const errorArg = args.find((a): a is Error => a instanceof Error);

	const formattedParts = args.map((arg) => {
		if (arg instanceof Error) {
			return `${arg.name}: ${arg.message}`;
		}
		if (typeof arg === 'string') {
			return arg;
		}
		if (typeof arg === 'object' && arg !== null) {
			try {
				return JSON.stringify(arg);
			} catch {
				return String(arg);
			}
		}
		return String(arg);
	});

	const fullMessage = formattedParts.join(' ');

	let title: string;
	if (errorArg) {
		title = errorArg.message ? `${errorArg.name}: ${errorArg.message}` : errorArg.name;
	} else if (typeof args[0] === 'string') {
		title = args[0].length > 120 ? `${args[0].slice(0, 117)}...` : args[0];
	} else {
		title =
			fullMessage.length > 120
				? `${fullMessage.slice(0, 117)}...`
				: fullMessage || 'Console log';
	}

	let stack: string | undefined;
	if (errorArg?.stack) {
		stack = errorArg.stack;
	} else {
		stack = extractCallerStack();
	}

	let details: Record<string, unknown> | string | null = null;
	const objectArgs = args.filter(
		(a) => typeof a === 'object' && a !== null && !(a instanceof Error)
	);
	if (objectArgs.length === 1 && !Array.isArray(objectArgs[0])) {
		details = objectArgs[0] as Record<string, unknown>;
	} else if (args.length > 1) {
		details = {
			arguments: args.map((a) =>
				a instanceof Error
					? { name: a.name, message: a.message, stack: a.stack }
					: typeof a === 'object' && a !== null
						? (sanitizeDetails(a) as Record<string, unknown>)
						: a
			)
		};
	}

	return { title, message: fullMessage, stack, details };
}

function interceptConsoleMethod(
	methodName: 'error' | 'warn' | 'info' | 'log' | 'debug',
	severity: DiagnosticSeverity
): void {
	const original = console[methodName];
	console[methodName] = function (...args: unknown[]) {
		original.apply(console, args);

		if (isCapturingConsole) return;
		isCapturingConsole = true;
		try {
			// Skip internal framework / HMR messages
			if (
				typeof args[0] === 'string' &&
				(args[0].startsWith('[vite]') || args[0].startsWith('[HMR]'))
			) {
				return;
			}

			const { title, message, stack, details } = formatConsoleArgs(args);
			const url = typeof window !== 'undefined' ? window.location.href : undefined;
			const errorArg = args.find((a): a is Error => a instanceof Error);
			const isThirdParty = isThirdPartyIncident({ url, stack, message, title });
			const isFiltered = isFilteredError({
				errorType: errorArg ? errorArg.name || errorArg.constructor.name : undefined,
				message: errorArg?.message ?? (message || title)
			});

			addDiagnostic({
				severity,
				category: 'runtime',
				title,
				message,
				url,
				stack,
				details,
				isThirdParty: isThirdParty || isFiltered
			});

			if (
				severity === 'error' &&
				!isThirdParty &&
				!isFiltered &&
				typeof window !== 'undefined'
			) {
				if (errorArg) {
					const stringArgs = args.filter((a): a is string => typeof a === 'string');
					const consoleContext = stringArgs.join(' ').trim();
					const sentryContext: Record<string, unknown> = {
						extra: {
							...(consoleContext ? { consoleMessage: consoleContext } : {}),
							fullMessage: message || undefined,
							...(details ? { details } : {})
						}
					};
					if (consoleContext) {
						sentryContext.tags = { consolePrefix: consoleContext.slice(0, 100) };
					}
					window.Sentry?.captureException(errorArg, sentryContext);
				} else {
					window.Sentry?.captureMessage(message || title);
				}
			}
		} catch {
			// Fail-safe: never disrupt console output
		} finally {
			isCapturingConsole = false;
		}
	};
}

export function restoreConsole(): void {
	if (originalConsole) {
		console.error = originalConsole.error;
		console.warn = originalConsole.warn;
		console.info = originalConsole.info;
		console.log = originalConsole.log;
		console.debug = originalConsole.debug;
		originalConsole = null;
		listenerInitialized = false;
	}
}

let listenerInitialized = false;

export function initGlobalDiagnosticsListener(): void {
	if (listenerInitialized) {
		return;
	}
	listenerInitialized = true;

	if (!originalConsole) {
		originalConsole = {
			error: console.error,
			warn: console.warn,
			info: console.info,
			log: console.log,
			debug: console.debug
		};
		interceptConsoleMethod('error', 'error');
		interceptConsoleMethod('warn', 'warning');
		interceptConsoleMethod('info', 'info');
		interceptConsoleMethod('log', 'info');
		interceptConsoleMethod('debug', 'info');
	}

	if (browser) {
		window.addEventListener(
			'error',
			(event: ErrorEvent) => {
				if (!event.message) {
					return;
				}

				const errorType =
					event.error instanceof Error
						? event.error.name || event.error.constructor.name
						: event.constructor.name;

				if (
					isFilteredError({
						eventType: 'error',
						errorType,
						message: event.message
					})
				) {
					(originalConsole?.warn ?? console.warn)(
						`[Diagnostics] Filtered error ignored: ${event.message}`,
						event
					);
					event.stopImmediatePropagation();
					event.preventDefault();
					return;
				}

				const filename = event.filename !== '' ? event.filename : 'unknown';
				const line = event.lineno;
				const col = event.colno;

				addDiagnostic({
					severity: 'error',
					category: 'runtime',
					title: event.message,
					message: `${filename}:${line}:${col}`,
					url: window.location.href,
					stack: event.error?.stack
				});
			},
			true
		);

		window.addEventListener(
			'unhandledrejection',
			(event: PromiseRejectionEvent) => {
				const reason = event.reason;
				if (!reason) {
					return;
				}

				const message =
					reason instanceof Error
						? reason.message
						: typeof reason === 'string'
							? reason
							: 'Unhandled promise rejection';
				const errorType =
					reason instanceof Error ? reason.name || reason.constructor.name : undefined;

				if (
					isFilteredError({
						eventType: 'unhandledrejection',
						errorType,
						message
					})
				) {
					(originalConsole?.warn ?? console.warn)(
						`[Diagnostics] Filtered rejection ignored: ${message}`,
						reason
					);
					event.stopImmediatePropagation();
					event.preventDefault();
					return;
				}

				const stack = reason instanceof Error ? reason.stack : undefined;
				addDiagnostic({
					severity: 'error',
					category: 'runtime',
					title: 'Unhandled Promise Rejection',
					message,
					url: window.location.href,
					stack,
					details:
						typeof reason === 'object' && reason !== null
							? (reason as Record<string, unknown>)
							: null
				});
			},
			true
		);
	}
}
